import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useService} from '../../../../infrastructure/service/service';
import {SectionAccordionProps} from '../../../../entity/props/widget/section-accordion-props';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {Title} from '../../../../entity/model/title';
import {Section} from '../../../../entity/resource/section';
import {UserPermission} from '../../../../entity/enums/user-permission';
import DroppableContainer from '../document/DroppableContainer';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  Button
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Box from '@mui/material/Box';
import SectionDialog from '../title/SectionDialog';
import {FormikHelpers} from 'formik';
import {UniqueIdentifier} from '@dnd-kit/core';
import {AddSectionDto} from '../../../../entity/dto/add-section-dto';
import MergeDocumentDialog from '../title/MergeDocumentDialog';

import '../../../../assets/css/style.css';
import '../../../../assets/css/widget/section-accordion.css';

const SectionAccordion = (props: SectionAccordionProps): JSX.Element => {
  const {sectionService, userService, titleService, titleTemplateService, projectService} = useService();

  const [openAddSectionDialog, setOpenAddSectionDialog] = useState<boolean>(false);
  const [openMergeDocumentsDialog, setOpenMergeDocumentsDialog] = useState<boolean>(false);
  const [sectionOptions, setSectionOptions] = useState<DropdownOption[]>([]);
  const [isPermitted, setPermitted] = useState<boolean>(false);
  const [updatedSections, setUpdatedSections] = useState<Section[]>(props.sectionalContainers);
  const [title, setTitle] = useState<Title>();
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [mergePdfPermitted, setMergePdfPermitted] = useState<boolean>(false);

  useEffect(() => {
    const title = titleService.getTitle();
    setTitle(title);

    setSectionOptions(sectionService.getSectionOptions(props.project ? titleService.getTitle()?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? []));
    setPermitted(title ? projectService.isPermittedTitle(title.id) : true);
    setMergePdfPermitted(userService.isPermitted(UserPermission.MERGE_TO_PDF));
    setDocumentCount(Object.entries(props.items).filter(([key]: [string, UniqueIdentifier[]]): boolean => key !== props.nonSection?.id).reduce((sum: number, [, value]: [string, UniqueIdentifier[]]): number => sum + value.length, 0));
  }, [titleService.getTitle(), titleTemplateService.getTitleTemplate(), props.items]);

  useEffect(() => {
    setUpdatedSections(props.sectionalContainers);
  }, [props.sectionalContainers]);

  const handleAddNewSection = async (values: AddSectionDto, {setSubmitting}: FormikHelpers<AddSectionDto>): Promise<void> => {
    setSubmitting(true);

    let template;
    let response;

    if (props.project) {
      response = await titleService.addNewSection(props.project.createdByOrganizationID, title?.id ?? '', values.sections);
      if (props.project && response)
        titleService.setTitle(response);
    } else {
      template = titleTemplateService.getTitleTemplate();
      response = await sectionService.addNewSection(template?.createdByOrganizationID ?? '', template?.id ?? '', values.sections);

      if (response)
        titleTemplateService.setTitleTemplate(response);
    }

    if (response) {
      setOpenAddSectionDialog(false);
      setUpdatedSections(response.sections.filter((section: Section): boolean => section.name !== 'Unclassified') ?? props.sectionalContainers);
      props.setSectionalContainers(updatedSections);
      props.setItems(sectionService.getItems(response.sections));
      setSectionOptions(sectionService.getSectionOptions(response.sections));
    }

    setSubmitting(false);
  }

  const renderAccordionSection = (section: Section): JSX.Element => {
    return (
      <DroppableContainer
        project={props.project}
        items={props.items}
        id={section.id}
        activeId={props.activeId}
        key={section.id}
        isNonSection={false}
        setUpdatedSections={setUpdatedSections}
        setItems={props.setItems}
        label={section.id}
        minimal={false}/>
    );
  }

  return (
    <>
      <Box id={'section-container'} className={'section-container'}>
        {
          updatedSections.map(renderAccordionSection)
        }
      </Box>
      <Button
        id={'section-add-btn'}
        className={`section-button ${!isPermitted || sectionOptions.length === 0 ? 'disable' : ''}`}
        onClick={(): void => setOpenAddSectionDialog(true)}
        disabled={!isPermitted || sectionOptions.length === 0}>
        <PlaylistAddIcon className={'section-icon'}/>
          Add New Section
      </Button>
      {
        documentCount > 1 && isPermitted && mergePdfPermitted && props.project && title &&
          <Button
            className={'zitles-button merge-button'}
            onClick={(): void => setOpenMergeDocumentsDialog(true)}
            startIcon={<PictureAsPdfIcon/>}>
            Merge to PDF
          </Button>
      }
      {
        openAddSectionDialog &&
          <SectionDialog
            open={openAddSectionDialog}
            setOpen={setOpenAddSectionDialog}
            sectionOptions={sectionOptions}
            handleSubmit={handleAddNewSection}/>
      }
      {
        openMergeDocumentsDialog && title && props.project &&
          <MergeDocumentDialog
            open={openMergeDocumentsDialog}
            setOpen={setOpenMergeDocumentsDialog}
            project={props.project}
            selectedTitle={title}/>
      }
    </>
  );
}

export default observer(SectionAccordion);
